import { useEffect, useState } from "react";
import MeritoButton from "../../../../../components/common/MeritoButtons/MeritoButtons";
import StatusBadge from "../../../../../components/common/StatusBadge/StatusBadge";
import TruncateTooltip from "../../../../../components/common/TruncateTooltip/TruncateTooltip";
import GoogleSignIn from "../../../../../resources/images/google.png";
import { connectionHubApi } from "../../../api/connectionHub.api";
import type { QuickAccountRow } from "../../../api/connectionHub.types";
import { CONNECTION_STATUS_PRESENTATION } from "../../../constants/connectionStatus.constants";
import type { QuickConnectTarget } from "../../../hooks/useConnectLauncher";
import AccountsModal, { AccountRemoveButton } from "../AccountsModal/AccountsModal";
import styles from "./QuickConnectModal.module.scss";

const AMAZON_SIGN_IN = "https://images-na.ssl-images-amazon.com/images/G/01/lwa/btnLWA_gold_390x92.png";

export type QuickAccount = QuickAccountRow;

export const isExpiredQuickAccount = (account: QuickAccount): boolean => account.status === "EXPIRED";

export interface QuickConnectModalProps {
  visible: boolean;
  target: QuickConnectTarget;
  logo?: string | null;
  agencyClientId: number | null;
  onConnect: () => void;
  onRemoveAccount: (account: QuickAccount) => void;
  onClose: () => void;
}

export const quickAccountName = (account: QuickAccount): string =>
  account.storeName || account.clientName || account.customerName || account.customerId || "Account";

const quickAccountId = (account: QuickAccount): string | null =>
  account.customerId || account.clientId || null;

const QuickConnectModal = ({
  visible,
  target,
  logo,
  agencyClientId,
  onConnect,
  onRemoveAccount,
  onClose,
}: QuickConnectModalProps): JSX.Element => {
  const [accounts, setAccounts] = useState<QuickAccount[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const channelName = target.companyName ?? "this channel";

  useEffect(() => {
    if (!visible || !target._id) return;
    let active = true;
    setLoaded(false);
    setLoadFailed(false);
    connectionHubApi.getQuickAccounts(target._id, agencyClientId)
      .then((rows) => {
        if (active) setAccounts(rows);
      })
      .catch(() => {
        if (active) setLoadFailed(true);
      })
      .finally(() => {
        if (active) setLoaded(true);
      });
    return () => { active = false; };
  }, [agencyClientId, reloadKey, target._id, visible]);

  const hasExpired = accounts.some(isExpiredQuickAccount);

  const signIn = target.companyName?.includes("Google")
    ? { src: GoogleSignIn, alt: `Sign in with Google to connect ${channelName}` }
    : target.companyName?.includes("Amazon")
      ? { src: AMAZON_SIGN_IN, alt: `Login with Amazon to connect ${channelName}` }
      : null;

  return (
    <AccountsModal
      visible={visible}
      channelName={channelName}
      logo={logo}
      status={!loaded ? "loading" : loadFailed ? "failed" : "ready"}
      count={accounts.length}
      heading={hasExpired ? "Accounts" : "Connected Accounts"}
      emptyTitle={`Connect Your ${channelName} Account`}
      emptyAction={
        // Mirrors the legacy IpScreen: the provider's own sign-in button where one exists.
        signIn ? (
          <button type="button" className={styles.signInButton} onClick={onConnect} aria-label={signIn.alt}>
            <img className={`${styles.signIn} ${signIn.src === GoogleSignIn ? styles.googleSignIn : ""}`} src={signIn.src} alt="" />
          </button>
        ) : (
          <MeritoButton type="primary-1" btnText={`Connect ${channelName}`} onClick={onConnect} />
        )
      }
      onRetry={() => setReloadKey((key) => key + 1)}
      onConnectNew={onConnect}
      onClose={onClose}
    >
      <ul className={styles.list}>
        {accounts.map((account) => {
          const name = quickAccountName(account);
          const reference = quickAccountId(account);
          const status = CONNECTION_STATUS_PRESENTATION[account.status];
          const text = (
            <>
              <TruncateTooltip text={name} className={styles.primary} />
              {reference ? <span className={styles.secondary}>{reference}</span> : null}
            </>
          );
          return (
            <li className={styles.row} key={account.id}>
              {isExpiredQuickAccount(account) ? (
                <button type="button" className={`${styles.rowText} ${styles.rowMain}`} aria-label={`Reconnect ${name}`} onClick={onConnect}>
                  {text}
                </button>
              ) : (
                <div className={styles.rowText}>{text}</div>
              )}
              <StatusBadge label={status.label} variant={status.variant} spinner={status.spinner} />
              <AccountRemoveButton label={name} onClick={() => onRemoveAccount(account)} />
            </li>
          );
        })}
      </ul>
    </AccountsModal>
  );
};

export default QuickConnectModal;
